import { Send, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
  }).format(Number(n ?? 0));
}

/**
 * EmitDraftsPreview — rich preview card for EMIT_DRAFTS actions
 * triggered from the chat (emitir_borradores_batch tool).
 *
 * Shows count, total, and "sin NIF" warnings before user confirms emission.
 */
export default function EmitDraftsPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <Send className="w-3.5 h-3.5" />
        <span>Emisión aprobada — procesando borradores…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Emisión de borradores cancelada</span>
      </div>
    );
  }

  const facturas = preview?.facturas ?? [];
  const sinNif = preview?.sin_nif ?? 0;

  return (
    <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/20 p-4 space-y-3 my-2 max-w-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Badge variant="outline" className="text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight">
            Emitir {preview?.facturas_a_emitir ?? 0} borrador{(preview?.facturas_a_emitir ?? 0) !== 1 ? 'es' : ''}
          </p>
        </div>
        <span className="text-lg font-bold text-violet-700 dark:text-violet-300 shrink-0 tabular-nums">
          {formatEur(preview?.total)}
        </span>
      </div>

      {facturas.length > 0 && (
        <div className="text-[11px] text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
          {facturas.slice(0, 5).map((f, i) => (
            <p key={i} className="truncate">
              <span className="font-mono">{f.numero}</span> · {f.cliente}
            </p>
          ))}
          {facturas.length > 5 && (
            <p className="text-[10px] text-muted-foreground">…y {facturas.length - 5} más</p>
          )}
        </div>
      )}

      {sinNif > 0 && (
        <p className="text-[11px] text-warning">
          ⚠ {sinNif} sin NIF — se omitirán
        </p>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject}
          className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove}
          className="flex-1 h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white">
          <Send className="w-3 h-3 mr-1" />Emitir
        </Button>
      </div>
    </div>
  );
}
